import React from 'react';
import {designSystemToken, WizardLayout} from '@lightricks/react-design-system';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import AnalyticsService, {
  eventNames,
} from '@/services/analytics/AnalyticsService';
import Sentry from '@/services/sentry/Sentry';
import {FLOW_NAMES} from '@/lib/analytics/analyticsConstants';
import {setContentStudioOnboardingCompleted} from '@/utils/LtidStorage';
import {ROUTES} from '@/config/routesConstants';
import onboardingWizardStore, {
  onboardingWizardActions,
  OnboardingWizardState,
} from '@/views/content-studio/onboarding/onboardingWizardStore';
import useUpdateConsent from '@/hooks/content-studio/mutations/use-update-consent';
import useNavigation from '@/hooks/use-navigation';
import {contentStudioActions} from '@/stores/contentStudioStore';
import ConsentFormScreen, {
  CONSENT_FORM_VERSION,
} from '../components/concent-form/ConsentFormScreen';
import {FEATURE_ID, FeatureId} from '../constants';
import {
  ONBOARDING_STEPS,
  ONBOARDING_STEPS_ORDER,
  OnboardingStep,
  ONBOARDING_STEPS_PRESENTATION_ID,
} from './onboardingSteps';
import styles from './onboardingWizard.module.scss';
import FeatureSelectionMenu from './steps/FeatureSelection/FeatureSelectionMenu';
import getStepData from './utils/getStepData';

type SurveyAnalyticsData = {
  maxOptions: string;
  isReplyRequired: string;
  questionResponse: string;
  response_details: string;
};

const stepToSurveyAnalyticsMap: Record<
  OnboardingStep,
  (state: OnboardingWizardState) => SurveyAnalyticsData
> = {
  [ONBOARDING_STEPS.CONSENT_FORM]: (state) => ({
    maxOptions: '1',
    isReplyRequired: 'true',
    questionResponse: JSON.stringify(state.consentFormAccepted),
    response_details: '',
  }),
  [ONBOARDING_STEPS.FEATURE_SELECTION]: (state) => ({
    maxOptions: Object.keys(FEATURE_ID).length.toString(),
    isReplyRequired: 'true',
    questionResponse: JSON.stringify(state.selectedFeature),
    response_details: '',
  }),
};

const getSurveyAnalyticsData = (
  state: OnboardingWizardState
): SurveyAnalyticsData => {
  return stepToSurveyAnalyticsMap[state.step](state);
};

const sendSurveyAnalytics = (
  stepIndex: number,
  title: string,
  subtitle: string
) => {
  const state = onboardingWizardStore.getState();
  const flow = AnalyticsService.getOrCreateFlow(
    FLOW_NAMES.CONTENT_STUDIO.ONBOARDING
  );
  const surveyAnalyticsData = getSurveyAnalyticsData(state);
  const eventPayload = {
    response_id: flow.flow_id,
    question_location: `${stepIndex}`,
    presentation_id: ONBOARDING_STEPS_PRESENTATION_ID[state.step],
    question_id: state.step,
    question_title: title,
    question_subtitle: subtitle,
    is_reply_required: surveyAnalyticsData.isReplyRequired,
    max_options: surveyAnalyticsData.maxOptions,
    question_response: surveyAnalyticsData.questionResponse,
    response_details: surveyAnalyticsData.response_details,
  };

  AnalyticsService.dispatchEvent(
    eventNames.CONTENT_STUDIO_SURVEY_RESPONSE,
    eventPayload
  );
};

const getContentForStep = (
  step: OnboardingStep,
  onAccept: () => void,
  onNext: () => void
): React.ReactNode => {
  const contentByStepMap = {
    [ONBOARDING_STEPS.CONSENT_FORM]: <ConsentFormScreen onAccept={onAccept} />,
    [ONBOARDING_STEPS.FEATURE_SELECTION]: (
      <FeatureSelectionMenu onNext={onNext} />
    ),
  };
  return contentByStepMap[step];
};

const canGoToNextStep = (step: OnboardingStep): boolean => {
  const state = onboardingWizardStore.getState();
  const checkByStepMap = {
    [ONBOARDING_STEPS.CONSENT_FORM]: state.consentFormAccepted,
    [ONBOARDING_STEPS.FEATURE_SELECTION]: state.selectedFeature,
  };
  return !!checkByStepMap[step];
};

export default function OnboardingWizard() {
  const {step, selectedFeature} = onboardingWizardStore();
  const {uiInteraction} = useUiInteractionEvent();
  const {navigate} = useNavigation();
  const {mutateAsync: updateConsent} = useUpdateConsent();
  const handleNext = () => {
    const currentStepIndex = ONBOARDING_STEPS_ORDER.indexOf(step);
    uiInteraction({
      item_name: 'next',
      interaction_location: `onboarding_${step}`,
      flow_name: FLOW_NAMES.CONTENT_STUDIO.ONBOARDING,
      interaction_details: JSON.stringify(onboardingWizardStore.getState()),
    });

    sendSurveyAnalytics(currentStepIndex, title, description);

    if (currentStepIndex === ONBOARDING_STEPS_ORDER.length - 1) {
      if (!selectedFeature) {
        return;
      }
      setContentStudioOnboardingCompleted();
      const featureToRouteMap: Record<FeatureId, string> = {
        [FEATURE_ID.RESIZE]: ROUTES.CONTENT_LAB_REFORMAT,
        [FEATURE_ID.DUB]: ROUTES.CONTENT_LAB_TRANSLATE,
        [FEATURE_ID.REPHRASE]: ROUTES.CONTENT_LAB_REPHRASE,
      };
      navigate(featureToRouteMap[selectedFeature]);
      contentStudioActions.setIsOnboardingModalOpen(true);
      return;
    }
    const nextStep = ONBOARDING_STEPS_ORDER[currentStepIndex + 1];
    onboardingWizardActions.setStep(nextStep);
  };

  const {title, description, renderImage} = getStepData(step);

  const onAccept = async () => {
    try {
      await updateConsent(CONSENT_FORM_VERSION);
    } catch (error) {
      Sentry.captureException(error as object, {
        title: 'Ltx Scaler consent update error',
        error,
      });
    }
    onboardingWizardActions.setConsentFormAccepted(true);
    handleNext();
  };

  return (
    <div
      className={`${styles.onboardingWizard} ${
        step === ONBOARDING_STEPS.CONSENT_FORM ? styles.consentForm : ''
      }`}
    >
      <WizardLayout
        heading={title}
        subheading={description}
        progress={null}
        progressBarColor={designSystemToken('semantic.bg.neutral')}
        renderContent={() => getContentForStep(step, onAccept, handleNext)}
        onNext={handleNext}
        defaultFooterButtonProps={{hideNextButton: true}}
        isNextDisabled={!canGoToNextStep(step)}
        renderDesktopVisual={renderImage}
      />
    </div>
  );
}
